import {
  TransactionListItem,
  TransactionStatus,
  TransactionType,
} from '@commenda/api/new-sales-tax/transactions/types';
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Icons,
} from '@commenda/kakashi';
import { cn } from '@commenda/utils';
import getSymbolFromCurrency from 'currency-symbol-map';
import { DataPoint } from '../../../Corporation/common/DataPoint';
import {
  formatNumberWithCommasAndDecimals,
  getStateName,
  SALES_TAX_STATUS_TO_TEXT,
  TRANSACTION_SALE_ENUM_TO_TEXT,
} from '../../utils';

interface TransactionActionModalProps {
  onVoid: () => void;
  onCommit: () => void;
  onLock: () => void;
  isOpen: boolean;
  onClose: () => void;
  transaction: TransactionListItem;
  action: TransactionStatus;
}

const TransactionActionModal = ({
  onVoid,
  onCommit,
  onLock,
  isOpen,
  onClose,
  transaction,
  action,
}: TransactionActionModalProps) => {
  const getActionTitle = () => {
    if (action === TransactionStatus.VOIDED) {
      return 'Mark transaction as void?';
    }
    if (action === TransactionStatus.COMMITTED) {
      return 'Mark transaction as to be filed?';
    }
    return 'Mark transaction as locked?';
  };

  const getActionDescription = () => {
    if (action === TransactionStatus.VOIDED) {
      return 'This action will mark the transaction as void. Once void, this transaction will not be included in reporting or filings.';
    }
    if (action === TransactionStatus.COMMITTED) {
      return 'This action will mark the transaction as to be filed. Once marked as to be filed, this transaction will be included in reporting and filings.';
    }
    return 'This action will mark the transaction as locked. Once locked, this transaction will not be included in reporting or filings.';
  };

  const getActionButtonText = () => {
    if (action === TransactionStatus.VOIDED) {
      return 'Mark as void';
    }
    if (action === TransactionStatus.COMMITTED) {
      return 'Mark as to be filed';
    }
    return 'Mark as locked';
  };

  const onActionButtonClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    if (action === TransactionStatus.VOIDED) {
      onVoid();
    }
    if (action === TransactionStatus.COMMITTED) {
      onCommit();
    }
    if (action === TransactionStatus.LOCKED) {
      onLock();
    }
  };

  return (
    <Dialog open={isOpen}>
      <DialogContent
        className="min-w-[600px] p-0"
        hideCloseIcon
        preventCloseOnOutsideClick
      >
        <DialogHeader className="pb-1">
          <div className="flex flex-col gap-lg">
            <div
              className={cn(
                'flex items-center justify-center size-12 rounded-lg',
                action === TransactionStatus.VOIDED && 'bg-bg-error',
                action === TransactionStatus.COMMITTED && 'bg-bg-success',
                action === TransactionStatus.LOCKED && 'bg-utility-blue-50'
              )}
            >
              {action === TransactionStatus.VOIDED && (
                <Icons.AlertCircle className="size-5 text-text-error" />
              )}
              {action === TransactionStatus.COMMITTED && (
                <Icons.CheckCircle className="size-5 text-text-success" />
              )}
              {action === TransactionStatus.LOCKED && (
                <Icons.Lock01 className="size-5 text-utility-blue-500" />
              )}
            </div>
            <div className="flex flex-col gap-xs">
              <DialogTitle>{getActionTitle()}</DialogTitle>
              <p className="txt-small-regular text-text-tertiary mb-4">
                {getActionDescription()}
              </p>
            </div>
          </div>
        </DialogHeader>

        <div className="p-6">
          <div className="border border-border-primary rounded-lg overflow-hidden">
            <DataPoint
              title="Type"
              value={
                TRANSACTION_SALE_ENUM_TO_TEXT[
                  transaction.type as TransactionType
                ]
              }
              className="border-b"
            />
            <DataPoint
              title="Transaction Date"
              value={transaction.date}
              className="border-b"
            />
            <DataPoint
              title="Ship-to State"
              value={
                transaction.destinationState
                  ? getStateName(transaction.destinationState)
                  : '-'
              }
              className="border-b"
            />
            <DataPoint
              title="Status"
              value={SALES_TAX_STATUS_TO_TEXT[transaction.status]}
              className="border-b"
            />

            <DataPoint
              title="Amount"
              value={`${getSymbolFromCurrency(transaction.invoiceCurrency)} ${formatNumberWithCommasAndDecimals(transaction.subTotal || 0)}`}
              className="border-b"
            />
            <DataPoint
              title="Tax Collected"
              value={`${getSymbolFromCurrency(transaction.invoiceCurrency)} ${formatNumberWithCommasAndDecimals(transaction.taxCollected || 0)}`}
              className="border-b"
            />
            <DataPoint
              title="Total"
              value={`${getSymbolFromCurrency(transaction.invoiceCurrency)} ${formatNumberWithCommasAndDecimals(transaction.total || 0)}`}
            />
          </div>
        </div>

        <DialogFooter>
          <div className="flex gap-3 items-center justify-end">
            <Button
              variant="secondary"
              onClick={(e) => {
                e.stopPropagation();
                onClose();
              }}
              size="sm"
              type="button"
            >
              Cancel
            </Button>
            <Button
              size="sm"
              onClick={onActionButtonClick}
              variant={
                action === TransactionStatus.VOIDED
                  ? 'destructive-primary'
                  : 'primary'
              }
              type="button"
            >
              {getActionButtonText()}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default TransactionActionModal;
